import { html } from 'lit';
import { dateOnly } from '../../shared/ui/format.js';
import { updateAccount } from '../api/developer.js';
import { word } from '../accounts.js';
import { AccountView } from './account-view.js';

/**
 * The one thing an owner decides about a team: what it is called.
 *
 * Nothing else here is an owner's to change. The slug is derived from the name when the team was
 * made, the kind is how the team came to exist, and the OSS grant is an operator's judgement about a
 * project. They are shown, read-only, so the page says what the team is before asking what it should
 * be called. A personal team keeps its name and has no settings page; the frame sends it to its
 * overview.
 */
export class UserAccountSettings extends AccountView
{
   static section = 'settings';

   static properties = { saving: { state: true } };

   constructor()
   {
      super();
      this.saving = false;
   }

   get definition()
   {
      return {
         sections: {
            main: {
               fields: {
                  Name: {
                     label: { title: 'Name' },
                     note: 'What the team is called on every page, in the sidebar and on the invitation.',
                     type: 'text',
                     mandatory: true,
                     maxlength: 200,
                     value: this.team.name ?? ''
                  }
               }
            }
         }
      };
   }

   async save(values)
   {
      const name = String(values.Name ?? '').trim();

      if (!name)
      {
         return;
      }

      this.saving = true;

      try
      {
         await this.act(() => updateAccount(this.account, { name }), `Renamed the team to ${name}.`);
      }
      finally
      {
         this.saving = false;
      }
   }

   get body()
   {
      const team = this.team;

      return html`
         <lia-card title="Team" icon="fa-solid fa-people-group">
            <dl class="row mb-0">
               ${[
                  ['Name', team.name],
                  ['Slug', team.slug],
                  ['Kind', kindWord(team.kind)],
                  ['Created', dateOnly(team.createdAt)],
                  ['Open-source grant', team.ossGrant ? 'Granted: every repository is free' : 'Not granted']
               ].map(([label, value]) => html`
                  <dt class="col-sm-3 fw-normal text-body-secondary">${label}</dt>
                  <dd class="col-sm-9">${value}</dd>`)}
            </dl>
         </lia-card>
         <lia-card title="Rename" icon="fa-solid fa-pen">
            <lia-form
               .definition=${this.definition}
               ?busy=${this.saving || this.busy}
               submit-label="Rename the team"
               @lia-submit=${event => this.save(event.detail.values)}
            ></lia-form>
         </lia-card>`;
   }
}

function kindWord(kind)
{
   switch (kind)
   {
   case 'personal':
      return 'Personal team';
   case 'team':
      return 'Team';
   default:
      return word(kind);
   }
}

customElements.define('user-account-settings', UserAccountSettings);
